import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Alert,
    CircularProgress,
    Autocomplete,
    Card,
    CardContent,
    Divider,
    Chip,
    IconButton,
    InputAdornment,
    alpha,
    useTheme,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import {
    Add as AddIcon,
    Close as CloseIcon,
    CreditCard as CreditCardIcon,
    ShoppingCart as ShoppingCartIcon,
    CheckCircle as CheckCircleIcon,
    AccountBalance as AccountBalanceIcon,
    Remove as RemoveIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { organizationApi, Organization } from '../../utils/organizationApi';
import { purchaseCreditsApi } from '../../utils/purchaseCreditsApi';
import { useAuth } from '../../contexts/AuthContext';
import { SavedCreditCard } from '../../types/plan';
import { api } from '../../utils/api';
import toast from 'react-hot-toast';

interface AddCreditsForOrganizationModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const AddCreditsForOrganizationModal: React.FC<AddCreditsForOrganizationModalProps> = ({
    open,
    onClose,
    onSuccess,
}) => {
    const theme = useTheme();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
    const [creditsAmount, setCreditsAmount] = useState<number>(100);
    const [cardNumber, setCardNumber] = useState('');
    const [cardHolder, setCardHolder] = useState('');
    const [expirationDate, setExpirationDate] = useState('');
    const [securityCode, setSecurityCode] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [customerIdentity, setCustomerIdentity] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [savedCards, setSavedCards] = useState<SavedCreditCard[]>([]);
    const [useNewCard, setUseNewCard] = useState(true);
    const [selectedCardId, setSelectedCardId] = useState<string>('');

    useEffect(() => {
        if (open) {
            loadOrganizations();
            loadSavedCards();
            // Preencher dados do usuário automaticamente
            if (user?.email) {
                setCustomerEmail(user.email);
            }
        }
    }, [open, user]);

    const loadOrganizations = async () => {
        try {
            const orgs = await organizationApi.list();
            setOrganizations(orgs);
        } catch (err) {
            console.error('Error loading organizations:', err);
            toast.error('Erro ao carregar organizações');
        }
    };

    const loadSavedCards = async () => {
        try {
            const cards = await api.get<SavedCreditCard[]>('/Payment/GetSavedCards', { auth: true });
            setSavedCards(cards);
            
            // Se há cartões salvos, selecionar o padrão ou o primeiro
            if (cards.length > 0) {
                const defaultCard = cards.find((c: SavedCreditCard) => c.isDefault);
                const cardToSelect = defaultCard || cards[0];
                setSelectedCardId(cardToSelect.id);
                setUseNewCard(false); // Usar cartão salvo por padrão se existir
                
                // Preencher automaticamente os dados do cliente se disponíveis
                if (cardToSelect.customerCpfCnpj || cardToSelect.customerEmail) {
                    setCustomerIdentity(cardToSelect.customerCpfCnpj || customerIdentity);
                    setCustomerEmail(cardToSelect.customerEmail || customerEmail);
                }
            } else {
                setUseNewCard(true); // Usar novo cartão se não houver cartões salvos
            }
        } catch (error) {
            console.error('Erro ao carregar cartões salvos:', error);
            setUseNewCard(true);
        }
    };

    const handleDeleteCard = async (cardId: string) => {
        try {
            await api.delete(`/Payment/DeleteSavedCard/${cardId}`, { auth: true });
            loadSavedCards();
        } catch (error) {
            console.error('Erro ao deletar cartão:', error);
        }
    };

    const getCreditPrice = () => {
        // Preço fixo para whitelabel: R$ 0,10 por crédito
        return 0.10;
    };

    const calculateTotal = () => {
        const pricePerCredit = getCreditPrice();
        return (creditsAmount * pricePerCredit).toFixed(2);
    };

    const handleIncrement = () => {
        setCreditsAmount(prev => prev + 10);
    };

    const handleDecrement = () => {
        setCreditsAmount(prev => Math.max(10, prev - 10));
    };

    const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(event.target.value) || 0;
        setCreditsAmount(Math.max(0, value));
    };

    const formatCardNumber = (value: string) => {
        const cleaned = value.replace(/\D/g, '');
        const formatted = cleaned.match(/.{1,4}/g)?.join(' ') || cleaned;
        return formatted.substring(0, 19); // 16 dígitos + 3 espaços
    };

    const formatExpirationDate = (value: string) => {
        const cleaned = value.replace(/\D/g, '');
        if (cleaned.length >= 2) {
            // Limitar mês a 12
            const month = Math.min(parseInt(cleaned.substring(0, 2)) || 0, 12);
            const monthStr = month.toString().padStart(2, '0');
            if (cleaned.length >= 4) {
                return `${monthStr}/${cleaned.substring(2, 4)}`;
            }
            return monthStr + (cleaned.length > 2 ? '/' + cleaned.substring(2) : '');
        }
        return cleaned;
    };

    // Validação do número do cartão usando algoritmo de Luhn
    const validateCardNumber = (cardNumber: string): boolean => {
        const cleaned = cardNumber.replace(/\s/g, '');
        if (!cleaned || cleaned.length < 13 || cleaned.length > 19) {
            return false;
        }

        let sum = 0;
        let isEven = false;

        // Percorrer de trás para frente
        for (let i = cleaned.length - 1; i >= 0; i--) {
            let digit = parseInt(cleaned.charAt(i));

            if (isEven) {
                digit *= 2;
                if (digit > 9) {
                    digit -= 9;
                }
            }

            sum += digit;
            isEven = !isEven;
        }

        return sum % 10 === 0;
    };

    // Validação da data de vencimento
    const validateExpirationDate = (expirationDate: string): { valid: boolean; message?: string } => {
        if (!expirationDate || expirationDate.length !== 5) {
            return { valid: false, message: 'Data de validade inválida' };
        }

        const parts = expirationDate.split('/');
        if (parts.length !== 2) {
            return { valid: false, message: 'Data de validade inválida' };
        }

        const month = parseInt(parts[0]);
        const year = parseInt(parts[1]);

        // Validar mês
        if (isNaN(month) || month < 1 || month > 12) {
            return { valid: false, message: 'Mês inválido. Use um valor entre 01 e 12' };
        }

        // Validar ano
        if (isNaN(year) || year < 0 || year > 99) {
            return { valid: false, message: 'Ano inválido' };
        }

        // Converter para data completa (assumindo 2000-2099)
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear() % 100;
        const currentMonth = currentDate.getMonth() + 1;

        // Converter ano de 2 dígitos para 4 dígitos
        const fullYear = year < 50 ? 2000 + year : 1900 + year;

        // Verificar se a data não está expirada
        if (fullYear < currentDate.getFullYear() || 
            (fullYear === currentDate.getFullYear() && month < currentMonth)) {
            return { valid: false, message: 'Cartão expirado. Verifique a data de vencimento' };
        }

        return { valid: true };
    };

    const handleCardNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formatted = formatCardNumber(event.target.value);
        setCardNumber(formatted);
    };

    const handleExpirationDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formatted = formatExpirationDate(event.target.value);
        setExpirationDate(formatted);
    };

    const handleSecurityCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.replace(/\D/g, '').substring(0, 4);
        setSecurityCode(value);
    };

    const formatCPFCNPJ = (value: string) => {
        const cleaned = value.replace(/\D/g, '');

        if (cleaned.length <= 11) {
            // CPF: 000.000.000-00
            return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        } else {
            // CNPJ: 00.000.000/0000-00
            return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
        }
    };

    const handleCustomerIdentityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.replace(/\D/g, '');
        setCustomerIdentity(value);
    };

    const validateForm = () => {
        if (!selectedOrganization) {
            setError('Selecione uma organização');
            return false;
        }
        if (creditsAmount <= 0) {
            setError('Quantidade de créditos deve ser maior que zero');
            return false;
        }
        if (!useNewCard && !selectedCardId) {
            setError('Selecione um cartão salvo ou use um novo cartão');
            return false;
        }
        if (useNewCard) {
            if (!cardNumber || !validateCardNumber(cardNumber)) {
                setError('Número do cartão inválido. Verifique o número do cartão');
                return false;
            }
            if (!cardHolder || cardHolder.trim().length < 3) {
                setError('Nome do titular inválido');
                return false;
            }
            const expirationValidation = validateExpirationDate(expirationDate);
            if (!expirationValidation.valid) {
                setError(expirationValidation.message || 'Data de validade inválida');
                return false;
            }
            if (!securityCode || securityCode.length < 3 || securityCode.length > 4) {
                setError('Código de segurança inválido (CVV deve ter 3 ou 4 dígitos)');
                return false;
            }
        }
        if (!customerEmail || !customerEmail.includes('@')) {
            setError('Email do cliente inválido');
            return false;
        }
        // Validar CPF/CNPJ
        const cpfCnpjDigits = customerIdentity.replace(/\D/g, '');
        if (!cpfCnpjDigits || (cpfCnpjDigits.length !== 11 && cpfCnpjDigits.length !== 14)) {
            setError('CPF/CNPJ inválido. CPF deve ter 11 dígitos e CNPJ deve ter 14 dígitos.');
            return false;
        }
        
        // Validação básica de CPF (se for CPF)
        if (cpfCnpjDigits.length === 11) {
            const validateCPF = (cpf: string): boolean => {
                cpf = cpf.replace(/[^\d]+/g, '');
                if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;

                let sum = 0;
                for (let i = 0; i < 9; i++) {
                    sum += parseInt(cpf.charAt(i)) * (10 - i);
                }
                let rev = 11 - (sum % 11);
                if (rev === 10 || rev === 11) rev = 0;
                if (rev !== parseInt(cpf.charAt(9))) return false;

                sum = 0;
                for (let i = 0; i < 10; i++) {
                    sum += parseInt(cpf.charAt(i)) * (11 - i);
                }
                rev = 11 - (sum % 11);
                if (rev === 10 || rev === 11) rev = 0;
                if (rev !== parseInt(cpf.charAt(10))) return false;

                return true;
            };

            if (!validateCPF(customerIdentity)) {
                setError('CPF inválido. Por favor, verifique o número do CPF.');
                return false;
            }
        }
        return true;
    };

    const handleSubmit = async () => {
        setError(null);

        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);

            // Calcular o valor a enviar: multiplicar a quantidade de créditos pelo preço unitário
            const unitPrice = getCreditPrice(); // Retorna 0.10
            const calculatedValue = creditsAmount * unitPrice;

            // Processar pagamento através do endpoint do backend
            const paymentData: any = {
                creditsAmount: creditsAmount, // Quantidade de créditos (não o valor)
                cardNumber: useNewCard ? cardNumber.replace(/\s/g, '') : '',
                cardHolder: useNewCard ? cardHolder : '',
                expirationDate: useNewCard ? expirationDate : '',
                securityCode: useNewCard ? securityCode : '',
                brand: 'visa', // Você pode detectar a bandeira automaticamente
                customerName: useNewCard ? cardHolder : user?.name || '',
                customerEmail: customerEmail,
                customerIdentity: customerIdentity.replace(/\D/g, ''), // Remove caracteres não numéricos
                customerIdentityType: customerIdentity.length <= 11 ? 'CPF' : 'CNPJ',
                organizationId: selectedOrganization!.id, // Adicionar ID da organização
                // Se usar cartão salvo, enviar o ID do cartão (o backend busca o token)
                creditCardToken: useNewCard ? undefined : selectedCardId
            };

            const result = await purchaseCreditsApi.purchaseExtraCredits(paymentData);

            if (result.success) {
                setSuccess(true);
                toast.success(`${creditsAmount} créditos adicionados com sucesso para ${selectedOrganization!.name}!`);

                // Aguardar um pouco para mostrar mensagem de sucesso
                setTimeout(() => {
                    if (onSuccess) {
                        onSuccess();
                    }
                    handleClose();
                }, 2000);
            } else {
                setError(result.message || 'Erro ao processar pagamento');
            }
        } catch (err: any) {
            console.error('Erro ao processar pagamento:', err);
            setError(err?.message || 'Erro ao processar pagamento. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setSelectedOrganization(null);
            setCreditsAmount(100);
            setCardNumber('');
            setCardHolder('');
            setExpirationDate('');
            setSecurityCode('');
            setCustomerEmail('');
            setCustomerIdentity('');
            setError(null);
            setSuccess(false);
            setSavedCards([]);
            setUseNewCard(true);
            setSelectedCardId('');
            onClose();
        }
    };

    const quickAmounts = [100, 250, 500, 1000];

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    maxHeight: '90vh',
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pb: 1,
                borderBottom: 1,
                borderColor: 'divider',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccountBalanceIcon color="primary" sx={{ fontSize: 28 }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Adicionar Créditos para Organização
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} disabled={loading}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
                {success ? (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        py: 4,
                    }}>
                        <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'success.main' }}>
                            Compra realizada com sucesso!
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {creditsAmount} créditos foram adicionados para {selectedOrganization?.name}.
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Seleção de Organização */}
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                                    Selecionar Organização
                                </Typography>
                                <Autocomplete
                                    options={organizations}
                                    getOptionLabel={(option) => `${option.name} (${option.identifier})`}
                                    value={selectedOrganization}
                                    onChange={(_, value) => setSelectedOrganization(value)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Organização"
                                            placeholder="Selecione uma organização"
                                            required
                                        />
                                    )}
                                    disabled={loading}
                                />
                            </CardContent>
                        </Card>

                        {/* Seleção de Quantidade */}
                        <Card sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                                    Quantidade de Créditos
                                </Typography>

                                {/* Botões de Quantidade Rápida */}
                                <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                                    {quickAmounts.map((amount) => (
                                        <Chip
                                            key={amount}
                                            label={`${amount} créditos`}
                                            onClick={() => setCreditsAmount(amount)}
                                            variant={creditsAmount === amount ? 'filled' : 'outlined'}
                                            sx={{
                                                fontWeight: creditsAmount === amount ? 'bold' : 'normal',
                                                cursor: 'pointer',
                                                color: creditsAmount === amount && theme.palette.mode === 'dark' ? '#000' : undefined,

                                            }}
                                        />
                                    ))}
                                </Box>

                                {/* Campo de Quantidade Personalizada */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <IconButton
                                        onClick={handleDecrement}
                                        color="primary"
                                        sx={{
                                            border: 1,
                                            borderColor: 'primary.main',
                                        }}
                                    >
                                        <RemoveIcon />
                                    </IconButton>

                                    <TextField
                                        type="number"
                                        value={creditsAmount}
                                        onChange={handleAmountChange}
                                        inputProps={{ min: 1, max: 10000 }}
                                        sx={{
                                            flex: 1,
                                            '& input': {
                                                textAlign: 'center',
                                                fontSize: '1.5rem',
                                                fontWeight: 'bold',
                                            }
                                        }}
                                    />

                                    <IconButton
                                        onClick={handleIncrement}
                                        color="primary"
                                        sx={{
                                            border: 1,
                                            borderColor: 'primary.main',
                                        }}
                                    >
                                        <AddIcon />
                                    </IconButton>
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                {/* Resumo de Valores */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Preço unitário:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        R$ {getCreditPrice().toFixed(2)}
                                    </Typography>
                                </Box>

                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    p: 2,
                                    bgcolor: alpha(theme.palette.success.main, 0.1),
                                    borderRadius: 2,
                                }}>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                        Total:
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                        R$ {calculateTotal()}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Dados do Cartão */}
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <CreditCardIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                        Dados do Pagamento
                                    </Typography>
                                </Box>

                                {/* Seleção de cartão salvo ou novo */}
                                {savedCards.length > 0 && (
                                    <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                                        <FormLabel component="legend">Forma de pagamento</FormLabel>
                                        <RadioGroup 
                                            value={useNewCard ? 'new' : 'saved'} 
                                            onChange={(e) => {
                                                const newValue = e.target.value === 'new';
                                                setUseNewCard(newValue);
                                                if (!newValue && savedCards.length > 0) {
                                                    const defaultCard = savedCards.find((c: SavedCreditCard) => c.isDefault);
                                                    setSelectedCardId(defaultCard ? defaultCard.id : savedCards[0].id);
                                                }
                                            }}
                                        >
                                            <FormControlLabel value="saved" control={<Radio />} label={`Usar cartão salvo (${savedCards.length} disponível${savedCards.length > 1 ? 'eis' : ''})`} />
                                            <FormControlLabel value="new" control={<Radio />} label="Usar novo cartão" />
                                        </RadioGroup>
                                    </FormControl>
                                )}

                                {/* Lista de cartões salvos */}
                                {!useNewCard && savedCards.length > 0 && (
                                    <Box sx={{ mb: 3 }}>
                                        <List>
                                            {savedCards.map((card) => (
                                                <ListItem
                                                    key={card.id}
                                                    secondaryAction={
                                                        <IconButton edge="end" onClick={() => handleDeleteCard(card.id)}>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    }
                                                    sx={{
                                                        border: selectedCardId === card.id ? 2 : 1,
                                                        borderColor: selectedCardId === card.id ? 'primary.main' : 'divider',
                                                        borderRadius: 2,
                                                        mb: 1,
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => {
                                                        setSelectedCardId(card.id);
                                                        // Preencher automaticamente os dados do cliente se disponíveis
                                                        if (card.customerCpfCnpj || card.customerEmail) {
                                                            setCustomerIdentity(card.customerCpfCnpj || customerIdentity);
                                                            setCustomerEmail(card.customerEmail || customerEmail);
                                                        }
                                                    }}
                                                >
                                                    <Radio checked={selectedCardId === card.id} />
                                                    <ListItemText
                                                        primary={
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <Typography>{card.brand}</Typography>
                                                                <Typography>•••• {card.last4Digits}</Typography>
                                                                {card.isDefault && <Chip label="Padrão" size="small" color="primary" />}
                                                            </Box>
                                                        }
                                                        secondary={`${card.holderName} - Validade: ${card.expiryMonth}/${card.expiryYear}`}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                )}

                                {/* Formulário de novo cartão */}
                                {useNewCard && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <TextField
                                        fullWidth
                                        label="Número do Cartão"
                                        value={cardNumber}
                                        onChange={handleCardNumberChange}
                                        placeholder="0000 0000 0000 0000"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <CreditCardIcon />
                                                </InputAdornment>
                                            ),
                                        }}
                                        error={cardNumber.replace(/\s/g, '').length > 0 && !validateCardNumber(cardNumber)}
                                        helperText={cardNumber.replace(/\s/g, '').length > 0 && !validateCardNumber(cardNumber) 
                                            ? 'Número do cartão inválido' 
                                            : ''}
                                    />

                                    <TextField
                                        fullWidth
                                        label="Nome do Titular"
                                        value={cardHolder}
                                        onChange={(e) => setCardHolder(e.target.value.toUpperCase())}
                                        placeholder="COMO ESTÁ NO CARTÃO"
                                    />

                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <TextField
                                            fullWidth
                                            label="Validade"
                                            value={expirationDate}
                                            onChange={handleExpirationDateChange}
                                            placeholder="MM/AA"
                                            error={expirationDate.length > 0 && !validateExpirationDate(expirationDate).valid}
                                            helperText={expirationDate.length > 0 && !validateExpirationDate(expirationDate).valid 
                                                ? validateExpirationDate(expirationDate).message 
                                                : 'Formato: MM/AA'}
                                        />

                                        <TextField
                                            fullWidth
                                            label="CVV"
                                            value={securityCode}
                                            onChange={handleSecurityCodeChange}
                                            placeholder="000"
                                            type="password"
                                        />
                                    </Box>

                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <TextField
                                            fullWidth
                                            label="Email do Cliente"
                                            type="email"
                                            value={customerEmail}
                                            onChange={(e) => setCustomerEmail(e.target.value)}
                                            placeholder="seu@email.com"
                                        />

                                        <TextField
                                            fullWidth
                                            label="CPF/CNPJ"
                                            value={formatCPFCNPJ(customerIdentity)}
                                            onChange={handleCustomerIdentityChange}
                                            placeholder="000.000.000-00"
                                        />
                                    </Box>
                                    </Box>
                                )}

                                {useNewCard && (
                                    <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'info.light', borderRadius: 1 }}>
                                        <Typography variant="body2" color="black">
                                            💳 Este cartão será salvo automaticamente após o pagamento para uso em futuras compras
                                        </Typography>
                                    </Box>
                                )}

                                <Alert severity="info" icon={<CheckCircleIcon />} sx={{ mt: 2 }}>
                                    <Typography variant="body2">
                                        Seus dados estão protegidos com criptografia de nível bancário
                                    </Typography>
                                </Alert>
                            </CardContent>
                        </Card>

                        {error && (
                            <Alert severity="error" onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}
                    </Box>
                )}
            </DialogContent>

            {!success && (
                <DialogActions sx={{
                    px: 3,
                    py: 2,
                    borderTop: 1,
                    borderColor: 'divider',
                }}>
                    <Button
                        onClick={handleClose}
                        disabled={loading}
                        variant="outlined"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={loading || !selectedOrganization || creditsAmount <= 0}
                        variant="contained"
                        startIcon={loading ? <CircularProgress size={20} /> : <ShoppingCartIcon />}
                        sx={{
                            minWidth: 150,
                            color: theme.palette.mode === 'dark' ? '#000' : undefined,
                        }}
                    >
                        {loading ? 'Processando...' : 'Finalizar Compra'}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default AddCreditsForOrganizationModal;
